import { WorkspaceRole } from '@/types/auth.types';
import { Permission } from '@/types/permission';
import { SubscriptionType } from '@/types/shared';

/**
 * Calculate credits permissions based on role, subscription type, and member settings
 * @param role - The user's role in the workspace
 * @param subType - The subscription type
 * @param shouldShowCreditsToMembers - Whether credits should be visible to members
 * @returns Permission object with view and action flags
 */
export const calculateCreditsPermissions = (
  role: WorkspaceRole | undefined,
  subType: SubscriptionType | undefined,
  shouldShowCreditsToMembers: boolean,
): Permission => {
  if (!role) {
    return { view: false, action: false };
  }

  if (!subType) {
    return {
      view: shouldShowCreditsToMembers || role === WorkspaceRole.Admin,
      action: role === WorkspaceRole.Admin,
    };
  }

  if (
    subType === SubscriptionType.Free ||
    subType === SubscriptionType.Starter ||
    subType === SubscriptionType.Pro ||
    subType === SubscriptionType.Max
  ) {
    return { view: true, action: role === WorkspaceRole.Admin };
  } else if (subType === SubscriptionType.Team || subType === SubscriptionType.Enterprise) {
    return { view: shouldShowCreditsToMembers || role === WorkspaceRole.Admin, action: role === WorkspaceRole.Admin };
  }

  return { view: false, action: false };
};

/**
 * Calculate plan permissions based on role and subscription type
 * @param role - The user's role in the workspace
 * @param subType - The subscription type
 * @returns Permission object with view and action flags
 */
export const calculatePlanPermissions = (
  role: WorkspaceRole | undefined,
  subType: SubscriptionType | undefined,
): Permission => {
  if (!role || !subType) {
    return { view: false, action: false };
  }

  if (subType === SubscriptionType.Enterprise) {
    return { view: true, action: false };
  }

  return { view: true, action: role === WorkspaceRole.Admin };
};

/**
 * Calculate seats permissions based on role and subscription type
 * @param role - The user's role in the workspace
 * @param subType - The subscription type
 * @returns Permission object with view and action flags
 */
export const calculateSeatsPermissions = (
  role: WorkspaceRole | undefined,
  subType: SubscriptionType | undefined,
): Permission => {
  if (!role || !subType) {
    return { view: false, action: false };
  }

  if (
    subType === SubscriptionType.Free ||
    subType === SubscriptionType.Starter ||
    subType === SubscriptionType.Pro ||
    subType === SubscriptionType.Max
  ) {
    return { view: false, action: true };
  } else if (subType === SubscriptionType.Team || subType === SubscriptionType.Enterprise) {
    return { view: true, action: subType === SubscriptionType.Team && role === WorkspaceRole.Admin };
  }

  return { view: false, action: false };
};

/**
 * Calculate manage payments permissions based on role and subscription type
 * @param role - The user's role in the workspace
 * @param subType - The subscription type
 * @returns Permission object with view and action flags
 */
export const calculateManagePaymentsPermissions = (
  role: WorkspaceRole | undefined,
  subType: SubscriptionType | undefined,
): Permission => {
  if (!role || !subType) {
    return { view: false, action: false };
  }

  if (
    subType === SubscriptionType.Free ||
    subType === SubscriptionType.Starter ||
    subType === SubscriptionType.Pro ||
    subType === SubscriptionType.Max ||
    subType === SubscriptionType.Team
  ) {
    return { view: true, action: role === WorkspaceRole.Admin };
  }

  return { view: false, action: false };
};

/**
 * Calculate model blocking permissions based on subscription type and feature flag
 * @param subType - The subscription type
 * @param isModelBlockingEnabled - Whether model blocking is enabled for the workspace
 * @returns Permission object with view and action flags
 */
export const calculateModelBlockingPermissions = (
  subType: SubscriptionType | undefined,
  isModelBlockingEnabled: boolean | undefined,
): Permission => {
  if (!subType) {
    return { view: false, action: false };
  }

  const isEnabled =
    (subType === SubscriptionType.Enterprise || subType === SubscriptionType.Team) && !!isModelBlockingEnabled;
  return { view: isEnabled, action: isEnabled };
};
